/**
 * App signing check — the allowlist and the verdict (VAPT 2026-10-07
 * "Insufficient Anti-Tampering Protection").
 *
 * The native SigningCheck plugin only reports what the running binary is signed
 * with (Android: certificate SHA-256s, iOS: Team ID). The decision lives here, in
 * the JS bundle, so a wrong allowlist entry is fixed by OTA rather than by a store
 * build. Pure: no plugin, DOM or Datadog imports (tests/unit/utils/signing-policy.spec.js).
 *
 * Fail-open: an empty allowlist, a skipped check (debug build) or a native error
 * never blocks. Only a definite mismatch against a configured allowlist does, and
 * only while ENFORCE_SIGNING_CHECK is on.
 */

/**
 * Per platform. On since 2026-10-21 for the VAPT retest: genuine dev, TestFlight,
 * Appflow and store builds are all signed with the certificates / Team ID below,
 * so only re-signed copies are blocked. Store build 1.3.11 (265) shipped with both
 * off. Escape hatch: set a platform back to false and ship it by OTA.
 */
export const ENFORCE_SIGNING_CHECK = Object.freeze({ ios: true, android: true });

/**
 * SHA-256 of the certificates genuine Android builds are signed with, as shown in
 * Play Console > Setup > App signing (colon-hex or plain hex both accepted):
 * the app-signing key (store installs) and the upload key (QA installs of
 * upload-signed APKs).
 */
export const ANDROID_CERT_SHA256 = [
  // Upload key: Appflow-signed builds (QA / direct APK installs). Read from the
  // build 264 (1.3.11) APK with `apksigner verify --print-certs`, 2026-10-21.
  '73201abad8c185173fde5e2f7eb6fe1b69d6c8b05ef96e525066242aae1ffb62',
  // Play app-signing key: what Google re-signs Play Store installs with. Play
  // Console > App signing > App signing key certificate, 2026-10-21.
  'FF:65:35:89:2F:91:38:39:BB:67:B4:02:3A:FC:7B:EC:DD:E2:B5:62:7D:48:B4:F5:B3:95:3B:02:96:29:15:9F',
];

/** Apple Team ID genuine iOS builds are signed with (project.pbxproj DEVELOPMENT_TEAM). */
export const IOS_TEAM_IDS = ['4G2HYEZQRL'];

const HEX_SHA256 = /^[0-9a-f]{64}$/;

/** Lower-case plain hex, or null if the value is not a SHA-256. */
export function normalizeCertSha256(raw) {
  const hex = String(raw || '')
    .replace(/[:\s]/g, '')
    .toLowerCase();
  return HEX_SHA256.test(hex) ? hex : null;
}

/**
 * @param {{ status: string, platform?: string, certSha256?: string[], teamId?: string }} observed
 *   what the native plugin reported
 * @param {{ androidCerts?: string[], iosTeamIds?: string[] }} [allowlist]
 * @returns {'match'|'mismatch'|'unconfigured'|'skipped'|'error'}
 */
export function signingVerdict(
  observed,
  { androidCerts = ANDROID_CERT_SHA256, iosTeamIds = IOS_TEAM_IDS } = {},
) {
  if (!observed || observed.status === 'skipped') return 'skipped';
  if (observed.status !== 'observed') return 'error';

  if (observed.platform === 'android') {
    const allowed = androidCerts.map(normalizeCertSha256).filter(Boolean);
    if (allowed.length === 0) return 'unconfigured';
    const seen = (observed.certSha256 || []).map(normalizeCertSha256).filter(Boolean);
    if (seen.length === 0) return 'error';
    return seen.every(cert => allowed.includes(cert)) ? 'match' : 'mismatch';
  }

  if (observed.platform === 'ios') {
    if (iosTeamIds.length === 0) return 'unconfigured';
    if (!observed.teamId) return 'error';
    return iosTeamIds.includes(observed.teamId) ? 'match' : 'mismatch';
  }

  return 'error';
}
